import { Button } from '@mm/components';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import TagModel from '@mm/state/model/TagModel';
import { Tag } from 'antd';
import classNames from 'classnames';
import { trimStart } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import FilterableSelect from '../../../../../../../../common/components/FilterableSelect/FilterableSelect';
import TextInput from '../../../../../../../../common/components/TextInput';
import styles from './TagsSelector.module.css';

type Props = {
  fbPageId: number;
  tags: TagModel[];
  availableTags: TagModel[];
  onChange: (tag: TagModel) => void;
  onTagRemove: (tag: TagModel) => void;
};

export const TagsSelector = ({
  tags,
  availableTags,
  onChange,
  onTagRemove,
  fbPageId,
}: Props) => {
  const { create } = useTagsCRUDActions();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQeury] = useState('');
  const trimmedQuery = useMemo(() => searchQuery.trim(), [searchQuery]);

  const onSearchQeury = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = trimStart(e.target.value);
      setSearchQeury(e.target.value);
    },
    [],
  );

  const onDropdownToggle = useCallback(
    () => setIsDropdownOpen(!isDropdownOpen),
    [isDropdownOpen],
  );

  const onSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (searchQuery) {
      if (tags.find(t => t.name === trimmedQuery)) {
        return;
      }
      const tag = await create(searchQuery.trim(), fbPageId, 'regular');
      setSearchQeury('');
      if (isDropdownOpen) {
        onDropdownToggle();
      }
      if (tag) {
        onChange(tag);
      }
    }
  };

  const onTagSelect = useCallback(
    async (_: string, tag?: TagModel) => {
      let newTag: TagModel | undefined;
      if (tag) {
        newTag = await create(tag.name || '', fbPageId, 'regular');
      }
      setSearchQeury('');
      if (isDropdownOpen) {
        onDropdownToggle();
      }

      if (newTag) {
        onChange(newTag);
      }
    },
    [isDropdownOpen, fbPageId, create, onChange, onDropdownToggle],
  );

  const suggestions = useMemo(
    () => [
      {
        title: '',
        suggestions: tags
          ? availableTags.filter(e => !tags.some(tag => tag.name === e.name))
          : availableTags,
      },
    ],
    [availableTags, tags],
  );

  return (
    <div className={styles.tagsSelectorContainer}>
      <form className={styles.tagsSelectorForm} onSubmit={onSubmitForm}>
        <div className={styles.tagsSelectorInputWrapper}>
          <label className={styles.tagsSelectorLabel}>
            <TextInput
              className={styles.tagsSelectorInput}
              name="name"
              value={searchQuery}
              autoComplete="false"
              readOnly
              placeholder="Add Tag"
              onFocus={onDropdownToggle}
            />
          </label>
          <Button
            css={{
              container: classNames(styles.addButton, {
                [styles.addButtonDropdownOpen]: isDropdownOpen,
              }),
            }}
            color="secondary-blue"
            type="submit"
            onClick={e => e?.stopPropagation()}
            disabled={!searchQuery || !!tags.find(t => t.name === trimmedQuery)}
          >
            Add
          </Button>
          {isDropdownOpen && (
            <FilterableSelect
              showSuggestOnly={true}
              defaultValue={searchQuery}
              className={styles.tagsSelectorAutocomplete}
              onBlur={onDropdownToggle}
              onInput={onSearchQeury}
              onChange={onTagSelect}
              name="filterable"
              searchPlaceholder="Add Tag"
              suggestions={suggestions}
            />
          )}
        </div>
      </form>
      <div className={styles.tagsList}>
        {tags.map(tag => (
          <Tag
            key={tag.name}
            color="geekblue"
            onClose={() => onTagRemove(tag)}
            closable={tag.closable === undefined ? true : tag.closable}
          >
            {tag.name}
          </Tag>
        ))}
      </div>
    </div>
  );
};
